/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * GaugeLinear - Barra linear para níveis, voltagem, pressão
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';

const GaugeLinear = ({
  value = 0,
  min = 0,
  max = 100,
  unit = '',
  label = '',
  zones = [],
  decimalPlaces = 1,
  showLabel = true,
  showUnit = true,
  orientation = 'horizontal', // 'horizontal' ou 'vertical'
  width = 200,
  height = 60,
}) => {
  // Calcular porcentagem do valor
  const percentage = useMemo(() => {
    const clampedValue = Math.max(min, Math.min(max, value));
    return ((clampedValue - min) / (max - min)) * 100;
  }, [value, min, max]);

  // Encontrar a cor da zona atual
  const currentZoneColor = useMemo(() => {
    if (!zones || zones.length === 0) return '#22c55e';
    const zone = zones.find(z => value >= z.min_value && value <= z.max_value);
    return zone?.color || '#22c55e';
  }, [value, zones]);

  // Gerar segmentos das zonas para o fundo
  const zoneSegments = useMemo(() => {
    if (!zones || zones.length === 0) {
      return [{ start: 0, end: 100, color: '#374151' }];
    }
    
    return zones.map(zone => ({
      start: ((zone.min_value - min) / (max - min)) * 100,
      end: ((zone.max_value - min) / (max - min)) * 100,
      color: zone.color,
      label: zone.label,
    }));
  }, [zones, min, max]);

  const formattedValue = typeof value === 'number' 
    ? value.toFixed(decimalPlaces)
    : '--';

  const isHorizontal = orientation === 'horizontal';
  const barHeight = isHorizontal ? 24 : height - 60;

  return (
    <div 
      className="flex flex-col gap-2 p-3 bg-gray-900 rounded-lg border border-gray-700"
      style={{ 
        width: isHorizontal ? width : 'auto',
        minHeight: isHorizontal ? height : 'auto',
      }}
    >
      {/* Label - linha separada no topo, sem truncamento */}
      {showLabel && label && (
        <div 
          className="text-gray-300 font-medium text-sm"
          title={label}
        >
          {label}
        </div>
      )}

      {/* Barra + Valor */}
      <div 
        className="flex items-center gap-3"
        style={{ flexDirection: isHorizontal ? 'row' : 'column' }}
      >
        {/* Barra */}
        <div 
          className="relative bg-gray-800 rounded overflow-hidden flex-1"
          style={{ 
            width: isHorizontal ? '100%' : 24,
            height: barHeight,
            minWidth: isHorizontal ? 80 : 24,
          }}
        >
          {/* Segmentos de zona (fundo) */}
          {zoneSegments.map((segment, index) => (
            <div
              key={index}
              className="absolute opacity-30"
              style={{
                [isHorizontal ? 'left' : 'bottom']: `${segment.start}%`,
                [isHorizontal ? 'width' : 'height']: `${segment.end - segment.start}%`,
                [isHorizontal ? 'height' : 'width']: '100%',
                [isHorizontal ? 'top' : 'left']: 0,
                backgroundColor: segment.color,
              }}
            />
          ))}

          {/* Barra de valor */}
          <div
            className="absolute rounded transition-all duration-300"
            style={{
              [isHorizontal ? 'left' : 'bottom']: 0,
              [isHorizontal ? 'width' : 'height']: `${percentage}%`,
              [isHorizontal ? 'height' : 'width']: '100%',
              [isHorizontal ? 'top' : 'left']: 0,
              backgroundColor: currentZoneColor,
              boxShadow: `0 0 10px ${currentZoneColor}60`,
            }}
          />

          {/* Marcações de escala */}
          {[0, 25, 50, 75, 100].map(mark => (
            <div
              key={mark}
              className="absolute bg-gray-600"
              style={{
                [isHorizontal ? 'left' : 'bottom']: `${mark}%`,
                [isHorizontal ? 'width' : 'height']: 1,
                [isHorizontal ? 'height' : 'width']: '100%',
                opacity: mark === 0 || mark === 100 ? 0 : 0.5,
              }}
            />
          ))}

          {/* Indicador de posição atual */}
          <div
            className="absolute bg-white transition-all duration-300"
            style={{
              [isHorizontal ? 'left' : 'bottom']: `${percentage}%`,
              [isHorizontal ? 'width' : 'height']: 2,
              [isHorizontal ? 'height' : 'width']: '100%',
              transform: isHorizontal ? 'translateX(-1px)' : 'translateY(1px)',
              boxShadow: '0 0 4px rgba(255,255,255,0.5)',
            }}
          />
        </div>

        {/* Valor e unidade */}
        <div 
          className="flex items-baseline gap-1 flex-shrink-0"
        >
          <span 
            className="font-mono font-bold"
            style={{ 
              fontSize: 18,
              color: currentZoneColor,
              textShadow: `0 0 8px ${currentZoneColor}40`,
            }}
          >
            {formattedValue}
          </span>
          {showUnit && unit && (
            <span className="text-gray-400 text-sm">
              {unit}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GaugeLinear;
